import { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Header from "../../components/ui/Header";
import Select from "../../components/ui/Select";
import ProjectCard from "./components/ProjectCard";
import ProjectDetailModal from "./components/ProjectDetailModal";
import ProjectFilters from "./components/ProjectFilters";
import ProjectStats from "./components/ProjectStats";
import { FilterState, Project, SortOption } from "./types";

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<string>("featured");
  const [filters, setFilters] = useState<FilterState>({
    category: "All",
    technology: "All",
    complexity: "All",
    status: "All",
    searchQuery: ""
  });

  const projects: Project[] = [
    {
      id: "1",
      title: "E-Commerce Platform with AI Recommendations",
      description: "Full-stack e-commerce solution with machine learning-powered product recommendations and real-time inventory management",
      longDescription: `A comprehensive e-commerce platform built with modern web technologies, featuring an intelligent recommendation engine powered by collaborative filtering algorithms.\n\nThe platform includes a responsive React frontend, Node.js backend with Express, and MongoDB for data persistence. The recommendation system analyzes user behavior patterns and purchase history to suggest relevant products, increasing conversion rates by 35%.\n\nImplemented real-time inventory tracking, secure payment processing with Stripe integration, and an admin dashboard for comprehensive store management.`,
      category: "Web Development",
      technologies: [
        { name: "React", category: "Frontend", icon: "react" },
        { name: "TypeScript", category: "Frontend", icon: "typescript" },
        { name: "Node.js", category: "Backend", icon: "nodejs" },
        { name: "Express", category: "Backend", icon: "express" },
        { name: "MongoDB", category: "Database", icon: "mongodb" },
        { name: "TensorFlow", category: "AI/ML", icon: "tensorflow" },
        { name: "Docker", category: "DevOps", icon: "docker" },
        { name: "AWS", category: "DevOps", icon: "aws" }],

      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1150fc9c7-1764530677248.png",
      alt: "Modern e-commerce website interface showing product grid with shopping cart and recommendation section on desktop screen",
      githubUrl: "https://github.com/Muhammadninja?tab=repositories/Muhammadninja?tab=repositories",
      liveUrl: "https://demo-ecommerce.example.com",
      featured: true,
      complexity: "Advanced",
      status: "Completed",
      startDate: "2023-06-01",
      endDate: "2023-12-15",
      metrics: {
        linesOfCode: 45000,
        commits: 342,
        contributors: 3,
        duration: "6 months",
        stars: 127,
        forks: 34
      },
      challenges: [
        "Implementing efficient collaborative filtering algorithm that could handle large datasets without performance degradation",
        "Designing a scalable microservices architecture to handle peak traffic during sales events",
        "Ensuring PCI DSS compliance for secure payment processing and customer data protection",
        "Optimizing database queries for real-time inventory updates across multiple warehouses"],

      solutions: [
        "Implemented Redis caching layer for recommendation results, reducing API response time by 70%",
        "Utilized AWS Lambda for auto-scaling during traffic spikes, maintaining 99.9% uptime",
        "Integrated Stripe's secure payment APIs with comprehensive error handling and transaction logging",
        "Designed efficient database indexing strategy and implemented connection pooling for optimal query performance"],

      learnings: [
        "Deep understanding of machine learning recommendation systems and their practical applications in e-commerce",
        "Mastered microservices architecture patterns and inter-service communication strategies",
        "Gained expertise in payment gateway integration and security best practices",
        "Learned advanced database optimization techniques for high-traffic applications"],

      collaborators: [
        {
          name: "Sarah Johnson",
          role: "Backend Developer",
          avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_116022a91-1763299783599.png",
          alt: "Professional woman with brown hair in business casual attire smiling at camera"
        },
        {
          name: "David Chen",
          role: "ML Engineer",
          avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffbd3bcf-1763294660912.png",
          alt: "Asian man with glasses wearing blue shirt in office setting"
        }],

      academicProject: false
    },
    {
      id: "2",
      title: "Real-Time Collaborative Code Editor",
      description: "Web-based IDE with real-time collaboration, syntax highlighting, and integrated terminal for multiple programming languages",
      longDescription: `A sophisticated web-based integrated development environment that enables multiple developers to code together in real-time, similar to Google Docs but for programming.\n\nBuilt with React and WebSocket technology for instant synchronization, the editor supports over 20 programming languages with intelligent syntax highlighting and auto-completion. Features include integrated terminal access, version control integration, and customizable themes.\n\nThe application uses operational transformation algorithms to handle concurrent edits without conflicts, ensuring smooth collaboration even with high latency connections.`,
      category: "Web Development",
      technologies: [
        { name: "React", category: "Frontend", icon: "react" },
        { name: "TypeScript", category: "Frontend", icon: "typescript" },
        { name: "WebSocket", category: "Backend", icon: "websocket" },
        { name: "Node.js", category: "Backend", icon: "nodejs" },
        { name: "Monaco Editor", category: "Frontend", icon: "monaco" },
        { name: "Redis", category: "Database", icon: "redis" }],

      image: "https://images.unsplash.com/photo-1593720217529-01f0a5d09aed",
      alt: "Split screen showing code editor interface with multiple cursors and syntax highlighting on dark theme",
      githubUrl: "https://github.com/Muhammadninja?tab=repositories/Muhammadninja?tab=repositories",
      liveUrl: "https://demo-codeeditor.example.com",
      featured: true,
      complexity: "Advanced",
      status: "Maintained",
      startDate: "2023-09-01",
      endDate: "2024-01-20",
      metrics: {
        linesOfCode: 38000,
        commits: 287,
        contributors: 2,
        duration: "4 months",
        stars: 89,
        forks: 23
      },
      challenges: [
        "Implementing operational transformation algorithm to handle concurrent edits from multiple users",
        "Optimizing WebSocket connections to minimize latency and handle connection drops gracefully",
        "Integrating Monaco Editor while maintaining custom features and real-time collaboration",
        "Managing state synchronization across multiple clients with different network conditions"],

      solutions: [
        "Developed custom operational transformation implementation with conflict resolution strategies",
        "Implemented connection pooling and automatic reconnection with exponential backoff",
        "Created abstraction layer over Monaco Editor API for seamless integration with collaboration features",
        "Used Redis pub/sub for efficient message broadcasting and state management across server instances"],

      learnings: [
        "Mastered WebSocket protocol and real-time communication patterns",
        "Gained deep understanding of operational transformation and CRDT algorithms",
        "Learned advanced state management techniques for distributed systems",
        "Developed expertise in code editor APIs and syntax highlighting engines"],

      collaborators: [
        {
          name: "Emily Rodriguez",
          role: "Frontend Developer",
          avatar: "https://images.unsplash.com/photo-1668732038385-1717f3c72b6d",
          alt: "Young woman with long dark hair wearing red sweater smiling confidently"
        }],

      academicProject: false
    },
    {
      id: "3",
      title: "Student Performance Analytics Dashboard",
      description: "Data visualization platform analyzing student performance metrics with predictive analytics for early intervention",
      longDescription: `An academic project developed for Valencia College's Data Science course, this dashboard provides comprehensive analytics on student performance patterns and identifies at-risk students early.\n\nThe system processes historical grade data, attendance records, and assignment submissions to generate predictive models using scikit-learn. Interactive visualizations built with D3.js help educators identify trends and make data-driven decisions.\n\nImplemented machine learning models achieved 87% accuracy in predicting students who might need additional support, enabling proactive intervention strategies.`,
      category: "Data Science",
      technologies: [
        { name: "Python", category: "Backend", icon: "python" },
        { name: "Flask", category: "Backend", icon: "flask" },
        { name: "React", category: "Frontend", icon: "react" },
        { name: "D3.js", category: "Frontend", icon: "d3" },
        { name: "scikit-learn", category: "AI/ML", icon: "sklearn" },
        { name: "PostgreSQL", category: "Database", icon: "postgresql" }],

      image: "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74",
      alt: "Dashboard interface displaying colorful charts and graphs showing student performance metrics and analytics",
      githubUrl: "https://github.com/Muhammadninja?tab=repositories/Muhammadninja?tab=repositories",
      featured: false,
      complexity: "Intermediate",
      status: "Completed",
      startDate: "2023-08-15",
      endDate: "2023-12-10",
      metrics: {
        linesOfCode: 15000,
        commits: 156,
        contributors: 1,
        duration: "4 months"
      },
      challenges: [
        "Handling sensitive student data while ensuring FERPA compliance and data privacy",
        "Processing large datasets efficiently for real-time dashboard updates",
        "Selecting appropriate machine learning algorithms for accurate predictions with limited historical data",
        "Creating intuitive visualizations that non-technical educators could easily understand"],

      solutions: [
        "Implemented comprehensive data anonymization and encryption strategies",
        "Utilized pandas and NumPy for efficient data processing with optimized algorithms",
        "Experimented with multiple ML models and selected ensemble methods for best accuracy",
        "Designed user-friendly interface with interactive tooltips and contextual help"],

      learnings: [
        "Gained practical experience in data privacy regulations and ethical data handling",
        "Mastered data preprocessing techniques and feature engineering for ML models",
        "Learned to communicate complex data insights through effective visualizations",
        "Developed skills in model evaluation and hyperparameter tuning"],

      academicProject: true,
      courseCode: "COP 2800 - Data Science Fundamentals"
    },
    {
      id: "4",
      title: "Mobile Fitness Tracking Application",
      description: "Cross-platform mobile app for workout tracking, nutrition logging, and personalized fitness recommendations",
      longDescription: `A comprehensive fitness application built with React Native that helps users track workouts, monitor nutrition, and achieve their health goals through personalized recommendations.\n\nThe app integrates with device sensors to automatically track activities, uses computer vision to analyze food photos for calorie estimation, and provides AI-powered workout suggestions based on user goals and progress.\n\nFeatures include social sharing, achievement badges, and integration with popular fitness wearables for comprehensive health monitoring.`,
      category: "Mobile App",
      technologies: [
        { name: "React Native", category: "Mobile", icon: "react" },
        { name: "TypeScript", category: "Mobile", icon: "typescript" },
        { name: "Node.js", category: "Backend", icon: "nodejs" },
        { name: "MongoDB", category: "Database", icon: "mongodb" },
        { name: "TensorFlow Lite", category: "AI/ML", icon: "tensorflow" },
        { name: "Firebase", category: "Backend", icon: "firebase" }],

      image: "https://images.unsplash.com/photo-1625027589035-0844e7f91b1b",
      alt: "Smartphone displaying fitness app interface with workout statistics, progress charts, and exercise tracking features",
      githubUrl: "https://github.com/Muhammadninja?tab=repositories/Muhammadninja?tab=repositories",
      liveUrl: "https://apps.apple.com/fitness-tracker",
      featured: true,
      complexity: "Advanced",
      status: "In Progress",
      startDate: "2024-01-15",
      metrics: {
        linesOfCode: 32000,
        commits: 198,
        contributors: 2,
        duration: "3 months"
      },
      challenges: [
        "Implementing accurate activity recognition using device sensors with varying hardware capabilities",
        "Optimizing battery consumption while maintaining continuous activity tracking",
        "Training food recognition model that works accurately across different cuisines and lighting conditions",
        "Ensuring smooth performance on both iOS and Android with shared codebase"],

      solutions: [
        "Developed adaptive sampling algorithms that adjust sensor polling based on detected activity",
        "Implemented intelligent background task scheduling to minimize battery impact",
        "Created custom dataset with diverse food images and fine-tuned MobileNet for on-device inference",
        "Utilized platform-specific optimizations while maintaining code reusability through abstraction layers"],

      learnings: [
        "Mastered React Native development and cross-platform mobile architecture",
        "Gained expertise in mobile sensor APIs and activity recognition algorithms",
        "Learned on-device machine learning optimization techniques for mobile deployment",
        "Developed skills in mobile app performance profiling and optimization"],

      collaborators: [
        {
          name: "Michael Torres",
          role: "Mobile Developer",
          avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13539cf1a-1763294982283.png",
          alt: "Hispanic man with short black hair wearing casual blue shirt outdoors"
        }],

      academicProject: false
    },
    {
      id: "5",
      title: "Automated Testing Framework for Web Applications",
      description: "Comprehensive testing suite with visual regression testing, API testing, and CI/CD integration",
      longDescription: `A robust testing framework designed to automate end-to-end testing for web applications, reducing manual testing time by 80% while improving test coverage.\n\nThe framework combines Selenium for browser automation, custom visual regression algorithms for UI consistency checks, and REST API testing capabilities. Integrated with Jenkins for continuous integration, it automatically runs tests on every code commit.\n\nFeatures include parallel test execution, detailed reporting with screenshots, and intelligent test retry mechanisms for flaky tests.`,
      category: "DevOps",
      technologies: [
        { name: "Python", category: "Backend", icon: "python" },
        { name: "Selenium", category: "Testing", icon: "selenium" },
        { name: "Pytest", category: "Testing", icon: "pytest" },
        { name: "Docker", category: "DevOps", icon: "docker" },
        { name: "Jenkins", category: "DevOps", icon: "jenkins" },
        { name: "PostgreSQL", category: "Database", icon: "postgresql" }],

      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a6cb8683-1764530678008.png",
      alt: "Computer screen showing automated testing dashboard with green checkmarks and test results in terminal window",
      githubUrl: "https://github.com/Muhammadninja?tab=repositories/Muhammadninja?tab=repositories",
      featured: false,
      complexity: "Intermediate",
      status: "Completed",
      startDate: "2023-10-01",
      endDate: "2024-02-28",
      metrics: {
        linesOfCode: 22000,
        commits: 234,
        contributors: 1,
        duration: "5 months",
        stars: 45,
        forks: 12
      },
      challenges: [
        "Handling dynamic web elements and asynchronous operations in automated tests",
        "Implementing reliable visual regression testing that accounts for minor rendering differences",
        "Managing test data and maintaining test isolation across parallel executions",
        "Reducing false positives in flaky tests while maintaining test reliability"],

      solutions: [
        "Developed smart wait strategies using explicit waits and custom expected conditions",
        "Created pixel-perfect comparison algorithm with configurable tolerance thresholds",
        "Implemented database transaction rollback and containerized test environments for isolation",
        "Built intelligent retry mechanism with exponential backoff and failure pattern analysis"],

      learnings: [
        "Mastered test automation best practices and design patterns",
        "Gained expertise in CI/CD pipeline configuration and optimization",
        "Learned advanced Selenium techniques for handling complex web interactions",
        "Developed skills in test result analysis and quality metrics reporting"],

      academicProject: false
    },
    {
      id: "6",
      title: "Natural Language Processing Chatbot",
      description: "AI-powered customer service chatbot with intent recognition and context-aware responses",
      longDescription: `An intelligent chatbot system built for Valencia College's AI course project, capable of understanding natural language queries and providing contextually relevant responses.\n\nThe system uses transformer-based models for intent classification and entity extraction, with a custom dialogue management system that maintains conversation context. Trained on customer service transcripts, it handles common inquiries with 92% accuracy.\n\nImplemented sentiment analysis to detect frustrated customers and escalate to human agents when necessary, improving customer satisfaction scores by 28%.`,
      category: "Machine Learning",
      technologies: [
        { name: "Python", category: "Backend", icon: "python" },
        { name: "TensorFlow", category: "AI/ML", icon: "tensorflow" },
        { name: "BERT", category: "AI/ML", icon: "bert" },
        { name: "Flask", category: "Backend", icon: "flask" },
        { name: "React", category: "Frontend", icon: "react" },
        { name: "MongoDB", category: "Database", icon: "mongodb" }],

      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1aba9722d-1764530675552.png",
      alt: "Futuristic chatbot interface with AI assistant icon and conversation bubbles showing natural language interaction",
      githubUrl: "https://github.com/Muhammadninja?tab=repositories/Muhammadninja?tab=repositories",
      liveUrl: "https://demo-chatbot.example.com",
      featured: false,
      complexity: "Advanced",
      status: "Completed",
      startDate: "2023-09-01",
      endDate: "2023-12-15",
      metrics: {
        linesOfCode: 18000,
        commits: 167,
        contributors: 1,
        duration: "3.5 months"
      },
      challenges: [
        "Training accurate intent classification model with limited labeled training data",
        "Maintaining conversation context across multiple turns while handling topic switches",
        "Implementing real-time response generation with acceptable latency for user experience",
        "Handling ambiguous queries and out-of-scope questions gracefully"],

      solutions: [
        "Used transfer learning with pre-trained BERT model and fine-tuned on domain-specific data",
        "Developed state machine-based dialogue manager with context tracking and slot filling",
        "Optimized model inference with TensorFlow Serving and implemented response caching",
        "Created fallback mechanisms with clarification questions and confidence thresholds"],

      learnings: [
        "Gained deep understanding of transformer architectures and attention mechanisms",
        "Mastered NLP preprocessing techniques and text classification methods",
        "Learned dialogue system design patterns and conversation flow management",
        "Developed expertise in model deployment and serving optimization"],

      academicProject: true,
      courseCode: "COP 3530 - Artificial Intelligence"
    },
    {
      id: "7",
      title: "Blockchain-Based Supply Chain Tracker",
      description: "Decentralized application for transparent supply chain management with smart contract automation",
      longDescription: `A blockchain-powered supply chain management system that provides end-to-end visibility and traceability of products from manufacturer to consumer.\n\nBuilt on Ethereum blockchain with Solidity smart contracts, the system automatically executes agreements when predefined conditions are met, reducing paperwork and manual verification. Each product has a unique digital identity tracked through every stage of the supply chain.\n\nThe decentralized architecture ensures data immutability and transparency, preventing fraud and enabling instant verification of product authenticity.`,
      category: "Web Development",
      technologies: [
        { name: "Solidity", category: "Backend", icon: "solidity" },
        { name: "Ethereum", category: "Backend", icon: "ethereum" },
        { name: "React", category: "Frontend", icon: "react" },
        { name: "Web3.js", category: "Frontend", icon: "web3" },
        { name: "Node.js", category: "Backend", icon: "nodejs" },
        { name: "IPFS", category: "Database", icon: "ipfs" }],

      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b01d6d20-1764530676516.png",
      alt: "Digital blockchain network visualization with connected nodes and supply chain tracking interface overlay",
      githubUrl: "https://github.com/Muhammadninja?tab=repositories/Muhammadninja?tab=repositories",
      featured: false,
      complexity: "Advanced",
      status: "In Progress",
      startDate: "2024-02-01",
      metrics: {
        linesOfCode: 25000,
        commits: 142,
        contributors: 2,
        duration: "2 months"
      },
      challenges: [
        "Designing gas-efficient smart contracts to minimize transaction costs on Ethereum network",
        "Implementing secure key management for multiple stakeholders in supply chain",
        "Handling large product data while keeping blockchain storage costs manageable",
        "Ensuring system usability for non-technical supply chain participants"],

      solutions: [
        "Optimized smart contract code and used events for off-chain data storage",
        "Implemented hierarchical deterministic wallets with role-based access control",
        "Stored large files on IPFS and kept only content hashes on blockchain",
        "Created intuitive web interface with QR code scanning for easy product tracking"],

      learnings: [
        "Mastered Solidity programming and smart contract development best practices",
        "Gained expertise in blockchain architecture and consensus mechanisms",
        "Learned decentralized storage solutions and IPFS integration",
        "Developed skills in Web3 development and cryptocurrency wallet integration"],

      collaborators: [
        {
          name: "Alex Kumar",
          role: "Blockchain Developer",
          avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_148f1e531-1763296114804.png",
          alt: "Indian man with beard wearing white shirt in professional office environment"
        }],

      academicProject: false
    },
    {
      id: "8",
      title: "Weather Prediction System Using ML",
      description: "Machine learning model for accurate weather forecasting using historical data and atmospheric patterns",
      longDescription: `An academic project for Valencia College's Machine Learning course that predicts weather conditions using historical meteorological data and advanced ML algorithms.\n\nThe system combines multiple data sources including temperature, humidity, pressure, and wind patterns to generate accurate 7-day forecasts. Implemented ensemble learning techniques with Random Forest and Gradient Boosting models achieving 89% accuracy.\n\nFeatures interactive visualizations showing prediction confidence intervals and historical accuracy metrics, helping users understand forecast reliability.`,
      category: "Data Science",
      technologies: [
        { name: "Python", category: "Backend", icon: "python" },
        { name: "scikit-learn", category: "AI/ML", icon: "sklearn" },
        { name: "Pandas", category: "Backend", icon: "pandas" },
        { name: "Matplotlib", category: "Frontend", icon: "matplotlib" },
        { name: "Flask", category: "Backend", icon: "flask" },
        { name: "PostgreSQL", category: "Database", icon: "postgresql" }],

      image: "https://img.rocket.new/generatedImages/rocket_gen_img_19bd9bc0f-1764530676051.png",
      alt: "Weather forecast visualization showing temperature graphs, precipitation charts, and atmospheric pressure data",
      githubUrl: "https://github.com/Muhammadninja?tab=repositories/Muhammadninja?tab=repositories",
      featured: false,
      complexity: "Intermediate",
      status: "Completed",
      startDate: "2023-08-20",
      endDate: "2023-11-30",
      metrics: {
        linesOfCode: 12000,
        commits: 98,
        contributors: 1,
        duration: "3 months"
      },
      challenges: [
        "Handling missing data and outliers in historical weather datasets",
        "Selecting relevant features from hundreds of available meteorological variables",
        "Balancing model complexity with prediction accuracy and computational efficiency",
        "Validating model performance across different seasons and weather patterns"],

      solutions: [
        "Implemented multiple imputation techniques and robust outlier detection algorithms",
        "Used feature importance analysis and correlation studies to identify key predictors",
        "Applied cross-validation with time series split to prevent data leakage",
        "Created season-specific models and ensemble methods for improved accuracy"],

      learnings: [
        "Gained expertise in time series analysis and forecasting techniques",
        "Mastered feature engineering for meteorological data",
        "Learned ensemble learning methods and model stacking strategies",
        "Developed skills in model evaluation metrics for regression problems"],

      academicProject: true,
      courseCode: "COP 3003 - Machine Learning Applications"
    }];


  const sortOptions: SortOption[] = [
    { value: "featured", label: "Featured First" },
    { value: "recent", label: "Most Recent" },
    { value: "complexity-high", label: "Complexity: High to Low" },
    { value: "complexity-low", label: "Complexity: Low to High" },
    { value: "name-asc", label: "Name: A to Z" },
    { value: "name-desc", label: "Name: Z to A" }];


  const filteredAndSortedProjects = useMemo(() => {
    let filtered = projects.filter((project) => {
      const matchesCategory = filters.category === "All" || project.category === filters.category;
      const matchesTechnology =
        filters.technology === "All" ||
        project.technologies.some((tech) => tech.name === filters.technology);
      const matchesComplexity =
        filters.complexity === "All" || project.complexity === filters.complexity;
      const matchesStatus = filters.status === "All" || project.status === filters.status;
      const matchesSearch =
        filters.searchQuery === "" ||
        project.title.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
        project.description.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
        project.technologies.some((tech) =>
          tech.name.toLowerCase().includes(filters.searchQuery.toLowerCase())
        );

      return (
        matchesCategory &&
        matchesTechnology &&
        matchesComplexity &&
        matchesStatus &&
        matchesSearch);

    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "featured":
          return b.featured === a.featured ? 0 : b.featured ? 1 : -1;
        case "recent":
          return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
        case "complexity-high":
          const complexityOrder = { Advanced: 3, Intermediate: 2, Beginner: 1 };
          return complexityOrder[b.complexity] - complexityOrder[a.complexity];
        case "complexity-low":
          const complexityOrderLow = { Advanced: 3, Intermediate: 2, Beginner: 1 };
          return complexityOrderLow[a.complexity] - complexityOrderLow[b.complexity];
        case "name-asc":
          return a.title.localeCompare(b.title);
        case "name-desc":
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    });

    return filtered;
  }, [projects, filters, sortBy]);

  const projectStats = useMemo(() => {
    const completed = projects.filter((p) => p.status === "Completed").length;
    const inProgress = projects.filter((p) => p.status === "In Progress").length;
    const totalCommits = projects.reduce((sum, p) => sum + p.metrics.commits, 0);
    const totalLinesOfCode = projects.reduce((sum, p) => sum + p.metrics.linesOfCode, 0);

    return {
      totalProjects: projects.length,
      completedProjects: completed,
      inProgressProjects: inProgress,
      totalCommits,
      totalLinesOfCode
    };
  }, [projects]);

  const handleViewDetails = (project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedProject(null), 300);
  };

  const handleResetFilters = () => {
    setFilters({
      category: "All",
      technology: "All",
      complexity: "All",
      status: "All",
      searchQuery: ""
    });
  };

  const handleSortChange = (value: string | number) => {
    setSortBy(value as string);
  };

  return (
    <>
      <Helmet>
        <title>Projects - Muhammad Fotih | Full-Stack Developer Portfolio</title>
        <meta
          name="description"
          content="Explore Muhammad Fotih's portfolio of web development, mobile apps, machine learning, and data science projects. View live demos, GitHub repositories, and detailed case studies." />

      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-24 pb-16 px-4 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-4">
                <Icon name="FolderGit2" size={16} />
                Project Portfolio
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
                Building Solutions Through Code
              </h1>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                A collection of projects showcasing my journey from academic coursework to
                real-world applications. Each project represents challenges overcome, technologies
                mastered, and lessons learned.
              </p>
            </div>

            <ProjectStats {...projectStats} />

            <ProjectFilters
              filters={filters}
              onFilterChange={setFilters}
              onReset={handleResetFilters}
              totalProjects={projects.length}
              filteredCount={filteredAndSortedProjects.length} />


            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-muted-foreground">
                {filteredAndSortedProjects.length} project
                {filteredAndSortedProjects.length !== 1 ? "s" : ""} found
              </p>
              <div className="w-64">
                <Select
                  placeholder="Sort by"
                  options={sortOptions.map((option) => ({
                    value: option.value,
                    label: option.label
                  }))}
                  value={sortBy}
                  onChange={handleSortChange} />

              </div>
            </div>

            {filteredAndSortedProjects.length > 0 ?
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAndSortedProjects.map((project) =>
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onViewDetails={handleViewDetails} />

                )}
              </div> :

              <div className="text-center py-16">
                <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Search" size={48} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">No Projects Found</h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your filters or search query
                </p>
                <Button variant="outline" onClick={handleResetFilters}>
                  Clear All Filters
                </Button>
              </div>
            }

            <div className="mt-16 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-8 text-center">
              <h2 className="text-2xl font-bold text-foreground mb-4">
                Interested in Collaboration?
              </h2>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                I'm always excited to work on innovative projects and learn from experienced developers. Let's build something amazing together!
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Button
                  variant="default"
                  size="lg"
                  iconName="MessageCircle"
                  iconPosition="left"
                  onClick={() => window.open("https://wa.me/qr/MIPMGLGF4RZ5O1", "_blank")}>

                  Start a Conversation
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Github"
                  iconPosition="left"
                  onClick={() => window.open("https://github.com/Muhammadninja?tab=repositories/Muhammadninja?tab=repositories", "_blank")}>

                  View GitHub Profile
                </Button>
              </div>
            </div>
          </div>
        </main>

        <ProjectDetailModal
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={handleCloseModal} />

      </div>
    </>);

};

export default Projects;